// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState, xHeaderActions, xHeaderState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbar } from "@bg/XDL/Utilities";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    l1Links = "l1Links",
    l2Links = "l2Links",
}

interface L2LinkModel {
    fields: {
        destination: LinkFieldValue;
        overrideTitleL2: string;
    };
}

interface L1LinkModel {
    fields: {
        destination: LinkFieldValue;
        l2Links: L2LinkModel[];
        overrideTitle: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.l1Links]: L1LinkModel[];
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: state.xHeader.isSticky,
    categoriesPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
    categories: state.xHeader.headerLinks,
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xHeaderActions> &
    ResolveThunks<typeof mapDispatchToProps>;

interface CustomLinkModel {
    url: string | undefined;
    title: string | undefined;
    overrideTitleL2?: string;
}

interface CategoryModel {
    link: CustomLinkModel;
    children: CustomLinkModel[];
    overrideTitle: string;
}

export interface ShopByCategoryL1L2Styles {
    wrapper?: InjectableCss;
    catGroupWrapper?: InjectableCss;
    l1LinkStyles?: LinkPresentationProps;
    l2LinkStyles?: LinkPresentationProps;
    l2LinksWrapper?: InjectableCss;
    categoriesWrapper?: InjectableCss;
    allCategoriesLinkWrapper?: InjectableCss;
}

export const shopByCategoryL1L2Styles: ShopByCategoryL1L2Styles = {
    wrapper: {
        css: css`
            padding-top: 39px;
            position: relative;
            height: 100%;
        `,
    },
    categoriesWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            flex-wrap: wrap;
            margin-left: -15px;
            margin-right: -15px;
            overflow-y: auto;
            height: 275px;
            ${styledScrollbar(6)};
        `,
    },
    allCategoriesLinkWrapper: {
        css: css`
            position: absolute;
            right: 0;
            top: 250px;
            width: calc(100% / 6);
            display: flex;
            justify-content: flex-end;
        `,
    },
    catGroupWrapper: {
        css: css`
            max-height: 275px;
            min-width: 210px;
            margin-bottom: 39px;
        `,
    },
    l1LinkStyles: {
        typographyProps: {
            ellipsis: true,
            lineHeight: fontConfig.lineHeight.xSmall,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0.23px;
                height: 20px;
                display: block;
                width: 100%;
                padding: 0 15px;
                color: ${getColor("text.main")} !important;
                margin-bottom: 11px;
            `,
        },
    },
    l2LinkStyles: {
        typographyProps: {
            weight: fontConfig.weight.semiBold,
            color: "secondary",
            css: css`
                letter-spacing: 0.23px;
                padding: 0 15px;
                margin-bottom: 4px;
            `,
        },
    },
    l2LinksWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
            max-height: 214px;
        `,
    },
};

const styles = shopByCategoryL1L2Styles;

const ShopByCategoryL1L2: React.FC<Props> = ({ fields: { l1Links }, categoriesPageUrl, categories }) => {
    if (!l1Links?.length) {
        return <></>;
    }

    const closePopover = () => {
        const popover = document.getElementsByClassName("shop-by-cat-link")[0];
        (popover as any).click();
    };

    return (
        <>
            <Page as="div">
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.categoriesWrapper}>
                        {categories.map(
                            (category: CategoryModel, index: any) =>
                                ((category as any)?.excludeFromNavigation === false ||
                                    (category as any)?.excludeFromNavigation === undefined) &&
                                index !== categories.length - 1 && (
                                    // this index !== (categories.length - 1) is to hide the View All Categories link coming from the Navigation Drawer.
                                    <StyledWrapper
                                        {...styles.catGroupWrapper}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        css={css`
                                            ${styles.catGroupWrapper?.css};
                                            width: calc((100% / 6) * ${Math.ceil(category.children.length / 8) || 1});
                                        `}
                                    >
                                        <Link {...styles.l1LinkStyles} href={category.link.url} onClick={closePopover}>
                                            {category.overrideTitle || category.link.title}
                                        </Link>
                                        <StyledWrapper {...styles.l2LinksWrapper}>
                                            {category.children.map(
                                                (l2Link: CustomLinkModel, index: any) =>
                                                    l2Link &&
                                                    ((l2Link as any)?.excludeFromNavigation === false ||
                                                        (l2Link as any)?.excludeFromNavigation === undefined) && (
                                                        <Link
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            key={index}
                                                            {...styles.l2LinkStyles}
                                                            href={l2Link.url}
                                                            onClick={closePopover}
                                                            css={css`
                                                                ${styles.l2LinkStyles?.css};
                                                                width: calc(
                                                                    100% / ${Math.ceil(category.children.length / 8)}
                                                                );
                                                            `}
                                                        >
                                                            {l2Link.overrideTitleL2 || l2Link.title}
                                                        </Link>
                                                    ),
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ),
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.allCategoriesLinkWrapper}>
                        <StyledWrapper
                            css={css`
                                padding: 3px 15px;
                                background: #fff;
                            `}
                        >
                            <Link color="primary" onClick={closePopover} href={categoriesPageUrl}>
                                {translate("View All Categories")}
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Page>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Shop By Category - L1 & L2",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.l1Links,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields, state) => {
                const { type, value } = item.fields.destination;
                const { overrideTitle, title } = item.fields;
                if (type === "Page") {
                    const link = getPageLinkByNodeId(state, value);
                    return overrideTitle || link?.title;
                }
                if (type === "Category") {
                    if (overrideTitle) {
                        return overrideTitle;
                    }
                    if (value === emptyGuid) {
                        return "Products";
                    }
                    const categoryState = getCategoryState(state, value);
                    if (!categoryState.value) {
                        if (!categoryState.isLoading && value) {
                            return dispatch => {
                                dispatch(
                                    loadCategory({
                                        id: value,
                                    }),
                                );
                            };
                        }
                        return "";
                    }
                    return categoryState.value.shortDescription;
                }
                if (type === "Url") {
                    return title ?? value;
                }
                return value;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                    isRequired: true,
                },
                {
                    name: "overrideTitle",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: fields.l2Links,
                    editorTemplate: "ListField",
                    getDisplay: (item: HasFields, state) => {
                        const { type, value } = item.fields.destination;
                        const { overrideTitleL2, title } = item.fields;
                        if (type === "Page") {
                            const link = getPageLinkByNodeId(state, value);
                            return overrideTitleL2 || link?.title;
                        }
                        if (type === "Category") {
                            if (overrideTitleL2) {
                                return overrideTitleL2;
                            }
                            if (value === emptyGuid) {
                                return "Products";
                            }
                            const categoryState = getCategoryState(state, value);
                            if (!categoryState.value) {
                                if (!categoryState.isLoading && value) {
                                    return dispatch => {
                                        dispatch(
                                            loadCategory({
                                                id: value,
                                            }),
                                        );
                                    };
                                }
                                return "";
                            }
                            return categoryState.value.shortDescription;
                        }
                        if (type === "Url") {
                            return title ?? value;
                        }
                        return value;
                    },
                    defaultValue: [],
                    fieldDefinitions: [
                        {
                            name: "destination",
                            editorTemplate: "LinkField",
                            defaultValue: {
                                value: "",
                                type: "Page",
                            },
                            isRequired: true,
                        },
                        {
                            name: "overrideTitleL2",
                            editorTemplate: "TextField",
                            defaultValue: "",
                        },
                    ],
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategoryL1L2),
    definition,
};

export default widgetModule;
